/**
 * Показ вкладок сверки «Оплат» без сервера: `VITE_MOCK_API=1`.
 *
 * Поставщики, платежи и акты выдуманы в форме ответов `reconcile-api`;
 * суммы — в духе 19.10.2026 (§1 дополнения о сверке). Отметки, разборы и
 * загрузки живут в памяти вкладки. Правил раскладки здесь нет: план задан
 * готовым и только убывает, когда его отмечают.
 */

import { ApiError } from '@shell/api';
import type { PortalRequest } from '../../api';
import type {
  ActLine,
  ActStatus,
  ActView,
  Ledger,
  LedgerAct,
  LedgerRow,
  Overview,
  OverviewSupplier,
  Plan,
  PlanMark,
  Statement,
} from './api';

const ME = 'Пётр Соколов';

/* ── Деньги в копейках ──────────────────────────────────────────────── */

const k = (value: string) => Math.round(Number(value) * 100);
const s = (kopecks: number) => {
  const sign = kopecks < 0 ? '-' : '';
  const abs = Math.abs(kopecks);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
};
const sum = (values: string[]) => s(values.reduce((acc, v) => acc + k(v), 0));

/* ── Поставщики ─────────────────────────────────────────────────────── */

interface MockSupplier {
  id: number;
  name: string;
  inn: string | null;
  deferral: number | null;
  system: string;
  overdue: string;
  bank: string | null;
}

const SUPPLIERS: MockSupplier[] = [
  { id: 45, name: 'ООО "Продстар-Торговый Дом"', inn: '7802345671', deferral: 14, system: '286410.50', overdue: '198220.00', bank: '84312.40' },
  { id: 12, name: 'ООО"ТОРГОВАЯ КОМПАНИЯ "МИРАТОРГ"', inn: '7714123456', deferral: 21, system: '152908.12', overdue: '64000.00', bank: '152908.12' },
  { id: 22, name: 'ООО «Олимп»', inn: '7810987654', deferral: 14, system: '31200.00', overdue: '0.00', bank: '31200.00' },
  { id: 40, name: 'ООО «ТРИЭР»', inn: '7804111222', deferral: 5, system: '18930.00', overdue: '0.00', bank: '-12940.00' },
  { id: 27, name: 'ИП Павлова Е. А.', inn: null, deferral: 30, system: '17400.00', overdue: '0.00', bank: null },
  { id: 31, name: 'ИП Алтынова С. В.', inn: '781012345678', deferral: 7, system: '13389.00', overdue: '13389.00', bank: '4253.00' },
  { id: 18, name: 'ООО «Гурманин»', inn: '7806543210', deferral: 10, system: '0.00', overdue: '0.00', bank: '-22554.00' },
];

const IMMEDIATE = [
  { id: 90, name: 'Магазин/рынок' },
  { id: 91, name: 'ОЗОН' },
];

/* ── Выписки ────────────────────────────────────────────────────────── */

let statements: Statement[] = [
  {
    id: 1,
    file_name: 'Выписка_40702810_01.01.2026-18.10.2026.xlsx',
    period_from: '2026-01-01',
    period_to: '2026-10-18',
    rows: 1284,
    new_rows: 1284,
    debit_total: '18456230.55',
    uploaded_at: '2026-10-19T09:12:00+03:00',
    uploaded_by: ME,
  },
];

/* ── План: что в выписке ────────────────────────────────────────────── */

function markItem(
  invoice_id: number,
  supplier_id: number,
  number: string,
  doc_date: string,
  balance: string,
  paid_on: string,
  how: string,
  purpose: string,
  amount = balance,
): PlanMark {
  const supplier = SUPPLIERS.find((x) => x.id === supplier_id)!.name;
  return {
    invoice_id,
    supplier_id,
    supplier,
    number,
    doc_date,
    balance,
    paid_on,
    how,
    payment: { day: paid_on, amount, purpose },
  };
}

let openCount = 96;
let openTotal = '707412.36';

let marks: PlanMark[] = [
  markItem(5101, 45, 'ПТ-18842', '2026-09-16', '21090.46', '2026-10-01', 'точно', 'Оплата по накл. ПТ-18842 от 16.09.2026 за продукты, в т.ч. НДС'),
  markItem(5102, 45, 'ПТ-18907', '2026-09-19', '8840.10', '2026-10-03', 'точно', 'Оплата по накл. ПТ-18907 от 19.09.2026, в т.ч. НДС'),
  markItem(5103, 45, 'ПТ-19011', '2026-09-23', '12733.90', '2026-10-08', 'частями', 'Оплата по счетам ПТ-19011, ПТ-19012 за продукты питания', '20114.30'),
  markItem(5104, 45, 'ПТ-19012', '2026-09-23', '7380.40', '2026-10-08', 'частями', 'Оплата по счетам ПТ-19011, ПТ-19012 за продукты питания', '20114.30'),
  markItem(5201, 12, 'МР-776120', '2026-09-08', '9984.00', '2026-09-29', 'точно', 'Оплата по УПД МР-776120 от 08.09.2026 за мясную продукцию'),
  markItem(5202, 12, 'МР-776544', '2026-09-12', '6230.75', '2026-10-03', 'точно', 'Оплата по УПД МР-776544 от 12.09.2026 за мясную продукцию'),
  markItem(5203, 12, 'МР-777013', '2026-09-15', '4413.20', '2026-10-06', 'по акту сверки', 'Оплата по акту сверки на 30.09.2026'),
  markItem(5204, 12, 'МР-777402', '2026-09-19', '3010.00', '2026-10-06', 'по акту сверки', 'Оплата по акту сверки на 30.09.2026'),
  markItem(5301, 22, 'ОЛ-2231', '2026-09-20', '2880.00', '2026-10-04', 'точно', 'Оплата по накладной 2231 от 20.09.2026 овощи'),
  markItem(5302, 22, 'ОЛ-2290', '2026-09-24', '1960.00', '2026-10-08', 'точно', 'Оплата по накладной 2290 от 24.09.2026 овощи'),
  markItem(5303, 22, 'ОЛ-2317', '2026-09-27', '3104.50', '2026-10-11', 'точно', 'Оплата по накладной 2317 от 27.09.2026 овощи'),
  markItem(5401, 40, 'ТР-512', '2026-10-08', '2472.89', '2026-10-01', 'оплачено до накладной', 'Оплата по счету 512 от 01.10.2026 за упаковку'),
];

let partial = [
  {
    invoice_id: 5110,
    supplier_id: 45,
    supplier: 'ООО "Продстар-Торговый Дом"',
    number: 'ПТ-19150',
    doc_date: '2026-09-30',
    balance: '48220.00',
    covered: '40000.00',
    left: '8220.00',
  },
  {
    invoice_id: 5501,
    supplier_id: 27,
    supplier: 'ИП Павлова Е. А.',
    number: '118',
    doc_date: '2026-09-18',
    balance: '17400.00',
    covered: '10000.00',
    left: '7400.00',
  },
];

const unused = [
  {
    day: '2026-08-20',
    amount: '22554.00',
    left: '22554.00',
    supplier_id: 18,
    supplier: 'ООО «Гурманин»',
    payee: 'ООО "ГУРМАНИН"',
    purpose: 'Оплата по счету № 3307 от 19.08.2026 за мясную продукцию. В т.ч. НДС 10% 2050.36',
  },
  {
    day: '2026-10-01',
    amount: '31870.00',
    left: '12940.00',
    supplier_id: 40,
    supplier: 'ООО «ТРИЭР»',
    payee: 'ООО "ТРИЭР"',
    purpose: 'Предоплата по счету 512 от 01.10.2026 за упаковку и расходные материалы',
  },
];

let unknown = [
  { inn: '7811556677', payee: 'ООО "ВАКУМПАК"', count: 2, total: '18340.00' },
  { inn: '7801998877', payee: 'ООО "ТД ЛЕТО"', count: 1, total: '9600.00' },
];

const learnedInn = [{ supplier_id: 27, supplier: 'ИП Павлова Е. А.', inn: '780512345690' }];

function plan(): Plan {
  const marked = sum(marks.map((m) => m.balance));
  return {
    summary: {
      open_count: openCount,
      open_total: openTotal,
      marked_count: marks.length,
      marked_total: marked,
      after_count: openCount - marks.length,
      after_total: s(k(openTotal) - k(marked)),
      to_suppliers: 214,
      already_used: 168,
      period_to: statements.length ? statements[0].period_to : null,
    },
    marks: structuredClone(marks),
    partial: structuredClone(partial),
    unused: structuredClone(unused),
    unused_older: 4,
    unknown: structuredClone(unknown),
    learned_inn: structuredClone(learnedInn),
  };
}

function settle(items: { supplier_id: number; amount: string }[]) {
  openCount -= items.length;
  for (const item of items) {
    openTotal = s(k(openTotal) - k(item.amount));
    const supplier = SUPPLIERS.find((x) => x.id === item.supplier_id);
    if (supplier) supplier.system = s(Math.max(0, k(supplier.system) - k(item.amount)));
  }
}

/* ── Акты ───────────────────────────────────────────────────────────── */

const acts = new Map<number, ActView>();
let nextAct = 800;

function line(
  id: number,
  day: string,
  kind: ActLine['kind'],
  document: string,
  amount: string,
  match: ActLine['match'],
  issue: ActLine['issue'] = null,
): ActLine {
  return { id, day, kind, document, amount, match, issue };
}

function actStatus(act: ActView): ActStatus {
  if (act.read_state !== 'full') return 'partial';
  const open =
    act.lines.some((l) => l.issue && !l.issue.resolved) || act.extra.some((e) => !e.resolved);
  return open ? 'issues' : 'ok';
}

function openCountOf(act: ActView): number {
  return act.lines.filter((l) => l.issue && !l.issue.resolved).length + act.extra.filter((e) => !e.resolved).length;
}

acts.set(701, {
  id: 701,
  supplier_id: 31,
  supplier: 'ИП Алтынова С. В.',
  number: '112',
  act_date: '2026-09-30',
  period_from: '2026-08-01',
  period_to: '2026-09-30',
  opening: '0.00',
  closing: '8505.50',
  read_state: 'full',
  read_note: null,
  file_name: 'Акт сверки Алтынова 30.09.2026.pdf',
  status: 'issues',
  can_edit: true,
  totals: {
    act_closing: '8505.50',
    our_closing: '4253.00',
    diff: '4252.50',
    explained: '4252.50',
    unexplained: null,
    explained_fully: true,
  },
  lines: [
    line(1, '2026-08-01', 'delivery', 'Продажа (0078-042101 от 01.08.2026)', '6120.00', {
      type: 'invoice',
      id: 3040,
      label: 'накл. 0078-042101 от 01.08 на 6 120,00 ₽',
    }),
    line(2, '2026-08-12', 'payment', 'Платёжное поручение №1022', '6120.00', {
      type: 'bank',
      id: 88012,
      label: 'п/п № 1022 от 12.08 на 6 120,00 ₽',
    }),
    line(3, '2026-08-20', 'delivery', 'Продажа (0078-043320 от 20.08.2026)', '3373.38', null, {
      key: 'a701-1',
      kind: 'missing_invoice',
      text: 'В акте: отгрузка 20.08 на 3 373,38 ₽ (Продажа 0078-043320). В складском учёте такой нет — найдите бумагу и сдайте её фото в «Накладных» или попросите у поставщика копию.',
      resolved: null,
    }),
    line(4, '2026-09-19', 'delivery', 'Продажа (0078-044855 от 19.09.2026)', '14268.12', {
      type: 'invoice',
      id: 3051,
      label: 'накл. 0078-044855 от 19.09 на 13 389,00 ₽',
    }, {
      key: 'a701-2',
      kind: 'amount_differs',
      text: '№ 0078-044855 от 19.09: у поставщика 14 268,12 ₽, у нас 13 389,00 ₽ — разница 879,12 ₽.',
      resolved: null,
    }),
    line(5, '2026-09-24', 'payment', 'Платёжное поручение №1184', '9136.00', {
      type: 'bank',
      id: 88540,
      label: 'п/п № 1184 от 24.09 на 9 136,00 ₽',
    }),
  ],
  extra: [
    {
      key: 'a701-3',
      kind: 'invoice_not_in_act',
      text: 'Накладная № 0078-044990 от 29.09 на 2 100,00 ₽ есть у нас, в акте её нет.',
      date: '2026-09-29',
      amount: '2100.00',
      ref_id: 3060,
      resolved: { reason: 'двойной документ, удалён в StoreHouse', by: ME, at: '2026-10-19T11:40:00+03:00' },
    },
  ],
  file_url: null,
});

function simpleAct(
  id: number,
  supplierId: number,
  number: string,
  actDate: string,
  from: string,
  closing: string,
  ours: string,
  issues: ActLine[] = [],
  readState: ActView['read_state'] = 'full',
): ActView {
  const supplier = SUPPLIERS.find((x) => x.id === supplierId)!;
  const lines: ActLine[] = [
    line(id * 10 + 1, from, 'delivery', `УПД №97 от ${from.split('-').reverse().join('.')}`, '15400.00', {
      type: 'invoice',
      id: id * 10 + 1,
      label: 'накл. 97 на 15 400,00 ₽',
    }),
    line(id * 10 + 2, actDate, 'payment', 'Оплата (платёжное поручение №24)', '15400.00', {
      type: 'bank',
      id: id * 10 + 2,
      label: 'п/п № 24 на 15 400,00 ₽',
    }),
    ...issues,
  ];
  const diff = s(k(closing) - k(ours));
  const act: ActView = {
    id,
    supplier_id: supplierId,
    supplier: supplier.name,
    number,
    act_date: actDate,
    period_from: from,
    period_to: actDate,
    opening: '0.00',
    closing,
    read_state: readState,
    read_note:
      readState === 'full'
        ? null
        : 'Прочитаны номер, период и 2 строки из 9. Остальные строки — картинкой, без текста. Пришлите акт выгрузкой из программы поставщика.',
    file_name: `Акт сверки ${number}.pdf`,
    status: 'ok',
    can_edit: true,
    totals: {
      act_closing: closing,
      our_closing: ours,
      diff,
      explained: diff,
      unexplained: null,
      explained_fully: true,
    },
    lines,
    extra: [],
    file_url: null,
  };
  act.status = actStatus(act);
  return act;
}

acts.set(
  702,
  simpleAct(702, 45, 'ПТ-0930', '2026-09-30', '2026-07-01', '92532.40', '84312.40', [
    line(7023, '2026-09-12', 'delivery', 'Реализация (ПТ-18710 от 12.09.2026)', '6120.00', null, {
      key: 'a702-1',
      kind: 'missing_invoice',
      text: 'В акте: отгрузка 12.09 на 6 120,00 ₽ (Реализация ПТ-18710). В складском учёте такой нет.',
      resolved: null,
    }),
    line(7024, '2026-09-26', 'delivery', 'Реализация (ПТ-19099 от 26.09.2026)', '9100.00', {
      type: 'invoice',
      id: 5109,
      label: 'накл. ПТ-19099 от 26.09 на 7 000,00 ₽',
    }, {
      key: 'a702-2',
      kind: 'amount_differs',
      text: '№ ПТ-19099 от 26.09: у поставщика 9 100,00 ₽, у нас 7 000,00 ₽ — разница 2 100,00 ₽.',
      resolved: null,
    }),
  ]),
);
acts.set(703, simpleAct(703, 12, 'МР-30/09', '2026-09-30', '2026-07-01', '152908.12', '152908.12'));
acts.set(704, simpleAct(704, 22, '41', '2026-07-31', '2026-05-01', '18400.00', '18400.00'));
acts.set(705, simpleAct(705, 40, '7', '2026-09-30', '2026-07-01', '0.00', '0.00', [], 'partial'));

function lastAct(supplierId: number): OverviewSupplier['last_act'] {
  const list = [...acts.values()]
    .filter((a) => a.supplier_id === supplierId)
    .sort((a, b) => (b.act_date ?? '').localeCompare(a.act_date ?? ''));
  const act = list[0];
  if (!act) return null;
  const age = act.act_date ? (Date.now() - new Date(act.act_date).getTime()) / 86_400_000 : 0;
  return { id: act.id, act_date: act.act_date, status: act.status, issues: openCountOf(act), stale: age > 60 };
}

function ledgerActs(supplierId: number): LedgerAct[] {
  return [...acts.values()]
    .filter((a) => a.supplier_id === supplierId)
    .sort((a, b) => (b.act_date ?? '').localeCompare(a.act_date ?? ''))
    .map((a) => ({
      id: a.id,
      number: a.number,
      act_date: a.act_date,
      period_from: a.period_from,
      period_to: a.period_to,
      status: a.status,
      issues: openCountOf(a),
      read_state: a.read_state,
      file_name: a.file_name,
    }));
}

/* ── Обзор ──────────────────────────────────────────────────────────── */

function overview(): Overview {
  return {
    fresh: {
      statement_to: statements.length ? statements[0].period_to : null,
      statements: statements.length,
    },
    can_edit: true,
    suppliers: SUPPLIERS.map((x) => {
      const act = lastAct(x.id);
      const ghosts = x.id === 18 ? 1 : 0;
      return {
        id: x.id,
        name: x.name,
        inn: x.inn,
        system_debt: x.system,
        overdue: x.overdue,
        bank_debt: x.bank,
        last_act: act,
        issues: (act ? act.issues : 0) + ghosts,
      };
    }),
    immediate: IMMEDIATE,
  };
}

/* ── Лента ──────────────────────────────────────────────────────────── */

function row(partialRow: Partial<LedgerRow> & Pick<LedgerRow, 'kind' | 'key' | 'date' | 'title' | 'amount'>): LedgerRow {
  return {
    number: null,
    sign: '',
    balance: null,
    status: null,
    due: null,
    invoice_id: null,
    act_id: null,
    purpose: null,
    issues: [],
    ...partialRow,
  };
}

function issueOf(actId: number, key: string) {
  const act = acts.get(actId);
  if (!act) return [];
  const found = act.lines.find((l) => l.issue?.key === key)?.issue ?? act.extra.find((e) => e.key === key);
  if (!found) return [];
  return [{ key: found.key, kind: found.kind, text: found.text, resolved: found.resolved }];
}

function altynovaRows(): LedgerRow[] {
  const rows: LedgerRow[] = [];
  if (acts.has(701)) {
    const act = acts.get(701)!;
    rows.push(
      row({
        kind: 'act',
        key: 'act-701',
        date: '2026-09-30',
        title: 'Акт сверки за 01.08–30.09.2026',
        number: '112',
        amount: '8505.50',
        act_id: 701,
        status: act.status === 'ok' ? 'сошёлся' : `долг по акту 8 505,50 ₽`,
        issues:
          openCountOf(act) > 0
            ? [
                {
                  key: 'a701-debt',
                  kind: 'debt_differs',
                  text: 'По акту на 30.09 долг 8 505,50 ₽, по нашему учёту — 4 253,00 ₽. Разницу объясняют 2 строки ниже.',
                  resolved: null,
                },
              ]
            : [],
      }),
    );
  }
  rows.push(
    row({
      kind: 'bank',
      key: 'bank-88540',
      date: '2026-09-24',
      title: 'Оплата из банка',
      number: 'п/п 1184',
      amount: '9136.00',
      sign: '-',
      balance: '4253.00',
      purpose: 'Оплата по счету 0078-044855 от 19.09.2026 за продукты питания. НДС не облагается',
    }),
    row({
      kind: 'invoice',
      key: 'inv-3051',
      date: '2026-09-19',
      title: 'Накладная',
      number: '0078-044855',
      amount: '13389.00',
      sign: '+',
      balance: '13389.00',
      status: 'не оплачена',
      due: '2026-09-26',
      invoice_id: 3051,
      issues: issueOf(701, 'a701-2'),
    }),
    row({
      kind: 'ghost',
      key: 'ghost-701-3',
      date: '2026-08-20',
      title: 'Отгрузка из акта',
      number: 'Продажа 0078-043320',
      amount: '3373.38',
      sign: '+',
      act_id: 701,
      issues: issueOf(701, 'a701-1'),
    }),
    row({
      kind: 'mark',
      key: 'mark-771',
      date: '2026-08-14',
      title: 'Отметка «Оплачено» в системе',
      amount: '6120.00',
      sign: '-',
      purpose: `отметил ${ME}, накл. 0078-042101`,
    }),
    row({
      kind: 'bank',
      key: 'bank-88012',
      date: '2026-08-12',
      title: 'Оплата из банка',
      number: 'п/п 1022',
      amount: '6120.00',
      sign: '-',
      balance: '0.00',
      purpose: 'Оплата по счету 0078-042101 от 01.08.2026 за продукты питания. НДС не облагается',
    }),
    row({
      kind: 'invoice',
      key: 'inv-3040',
      date: '2026-08-01',
      title: 'Накладная',
      number: '0078-042101',
      amount: '6120.00',
      sign: '+',
      balance: '6120.00',
      status: 'оплачена',
      due: '2026-08-08',
      invoice_id: 3040,
    }),
  );
  return rows;
}

function genericRows(supplierId: number): LedgerRow[] {
  if (supplierId === 18) {
    return [
      row({
        kind: 'bank',
        key: 'bank-gurmanin',
        date: '2026-08-20',
        title: 'Оплата из банка',
        number: 'п/п 1040',
        amount: '22554.00',
        sign: '-',
        balance: '-22554.00',
        purpose: 'Оплата по счету № 3307 от 19.08.2026 за мясную продукцию. В т.ч. НДС 10% 2050.36',
        issues: [
          {
            key: 'g-1',
            kind: 'payment_without_invoice',
            text: 'Оплачено 22 554,00 ₽, а накладной на эту сумму в складском учёте нет. Скорее всего, товар пришёл, но не заведён: найдите бумагу и сдайте её фото в «Накладных».',
            resolved: null,
          },
        ],
      }),
    ];
  }
  const today = new Date();
  const day = (offset: number) => new Date(today.getTime() - offset * 86_400_000).toISOString().slice(0, 10);
  const amounts = ['21090.46', '8840.10', '12733.90', '7380.40', '15400.00', '9984.00', '6230.75', '4413.20'];
  const rows: LedgerRow[] = [];
  let balance = 0;
  const events: LedgerRow[] = [];
  amounts.forEach((value, index) => {
    const offset = 150 - index * 18;
    events.push(
      row({
        kind: 'invoice',
        key: `inv-${supplierId}-${index}`,
        date: day(offset),
        title: 'Накладная',
        number: `${supplierId}-${1800 + index}`,
        amount: value,
        sign: '+',
        status: index < 5 ? 'оплачена' : 'не оплачена',
        due: day(offset - 14),
        invoice_id: 5000 + supplierId * 10 + index,
      }),
    );
    if (index < 5) {
      events.push(
        row({
          kind: index === 2 && supplierId === 12 ? 'mark' : 'bank',
          key: `pay-${supplierId}-${index}`,
          date: day(offset - 12),
          title: index === 2 && supplierId === 12 ? 'Отметка «Оплачено» в системе' : 'Оплата из банка',
          number: index === 2 && supplierId === 12 ? null : `п/п ${900 + index}`,
          amount: value,
          sign: '-',
          purpose: `Оплата по накладной ${supplierId}-${1800 + index} за продукты питания, в т.ч. НДС`,
        }),
      );
    }
  });
  events.sort((a, b) => a.date.localeCompare(b.date));
  for (const event of events) {
    if (event.kind === 'invoice') balance += k(event.amount);
    if (event.kind === 'bank') balance -= k(event.amount);
    rows.push({ ...event, balance: event.kind === 'mark' ? null : s(balance) });
  }
  for (const act of ledgerActs(supplierId)) {
    if (!act.act_date) continue;
    const view = acts.get(act.id)!;
    rows.push(
      row({
        kind: 'act',
        key: `act-${act.id}`,
        date: act.act_date,
        title: act.period_from ? `Акт сверки за ${act.period_from.split('-').reverse().join('.')}–${act.act_date.split('-').reverse().join('.')}` : 'Акт сверки',
        number: act.number,
        amount: view.closing ?? '0.00',
        act_id: act.id,
        status: act.status === 'ok' ? 'сошёлся' : act.status === 'partial' ? 'прочитан не полностью' : 'есть расхождения',
        issues: view.lines
          .filter((l) => l.issue)
          .map((l) => ({ ...l.issue! })),
      }),
    );
  }
  return rows.sort((a, b) => b.date.localeCompare(a.date));
}

function ledger(id: number, params: URLSearchParams): Ledger {
  const supplier = SUPPLIERS.find((x) => x.id === id);
  const immediate = IMMEDIATE.find((x) => x.id === id);
  if (!supplier && !immediate) throw new ApiError(404, 'not found', 'Поставщик не найден.');
  const today = new Date().toISOString().slice(0, 10);
  const from = params.get('from') ?? `${today.slice(0, 4)}-01-01`;
  const to = params.get('to') ?? today;
  const all = supplier ? (id === 31 ? altynovaRows() : genericRows(id)) : [];
  const rows = all.filter((r) => r.date >= from && r.date <= to);
  const act = supplier ? lastAct(id) : null;
  const actView = act ? acts.get(act.id)! : null;
  const unmarked = id === 31 ? 1 : id === 45 ? 4 : 0;
  return {
    supplier: {
      id,
      name: supplier?.name ?? immediate!.name,
      inn: supplier?.inn ?? null,
      deferral_days: supplier?.deferral ?? null,
      deferral_source: supplier?.deferral ? 'памятка владельца' : null,
      settles_immediately: Boolean(immediate),
    },
    debts: {
      system: supplier?.system ?? '0.00',
      bank: supplier?.bank ?? null,
      bank_since: supplier?.bank ? '2026-01-01' : null,
      bank_anchor: supplier?.bank ? (id === 31 ? 'act' : 'start') : null,
      act: actView
        ? { value: actView.closing ?? '0.00', act_date: actView.act_date, act_id: actView.id, number: actView.number }
        : null,
      hint:
        unmarked > 0
          ? `В выписке ${unmarked} ${unmarked === 1 ? 'платёж, не отмеченный' : 'платежа, не отмеченных'} в системе — отметьте во вкладке «Выписка банка».`
          : id === 18
            ? 'Долга в системе нет, а по банку оплачено 22 554,00 ₽ — накладной на эти деньги в складском учёте нет.'
            : null,
    },
    can_edit: true,
    period: { from, to },
    rows,
    acts: supplier ? ledgerActs(id) : [],
  };
}

/* ── Разбор адресов ─────────────────────────────────────────────────── */

export async function mock(path: string, options: PortalRequest): Promise<unknown | undefined> {
  if (!path.startsWith('/api/reconcile/')) return undefined;
  const [pathname, search = ''] = path.split('?');
  const params = new URLSearchParams(search);
  const method = options.method ?? 'GET';
  const body = (options.body ?? {}) as Record<string, unknown>;

  if (pathname === '/api/reconcile/overview') return overview();
  if (pathname === '/api/reconcile/plan') return plan();

  if (pathname === '/api/reconcile/statements') {
    if (method === 'GET') return { statements: structuredClone(statements) };
    const name = params.get('name') ?? 'выписка.xlsx';
    if (!/\.xlsx$/i.test(name)) {
      throw new ApiError(
        400,
        'bad file',
        'Это не выписка СберБизнеса: ожидался Excel-файл «Выписка операций по лицевому счёту». В СберБизнесе: Выписки → выбрать счёт и период → «Excel».',
      );
    }
    const statement: Statement = {
      id: statements.length + 1,
      file_name: name,
      period_from: '2026-10-01',
      period_to: new Date().toISOString().slice(0, 10),
      rows: 112,
      new_rows: 9,
      debit_total: '1840220.10',
      uploaded_at: new Date().toISOString(),
      uploaded_by: ME,
    };
    statements = [statement, ...statements];
    return { statement, plan: plan() };
  }

  if (pathname === '/api/reconcile/mark' && method === 'POST') {
    const ids = (body.invoice_ids as number[]) ?? [];
    const picked = marks.filter((m) => ids.includes(m.invoice_id));
    const total = sum(picked.map((m) => m.balance));
    settle(picked.map((m) => ({ supplier_id: m.supplier_id, amount: m.balance })));
    marks = marks.filter((m) => !ids.includes(m.invoice_id));
    return {
      message: `Отмечено оплаченными: ${picked.length} накл. на ${total.replace('.', ',')} ₽.`,
      marked: picked.length,
      total,
      errors: [],
      plan: plan(),
    };
  }

  if (pathname === '/api/reconcile/mark-one' && method === 'POST') {
    const id = Number(body.invoice_id);
    const item = partial.find((p) => p.invoice_id === id);
    if (!item) throw new ApiError(409, 'conflict', 'Эту накладную уже отметили.');
    const paid = body.amount === null ? item.balance : String(body.amount);
    settle([{ supplier_id: item.supplier_id, amount: paid }]);
    partial = partial.filter((p) => p.invoice_id !== id);
    return {
      message:
        body.amount === null
          ? `Накладная ${item.number} отмечена оплаченной полностью.`
          : `Накладная ${item.number}: записана оплата ${paid.replace('.', ',')} ₽ от ${String(body.paid_on).split('-').reverse().join('.')}.`,
      plan: plan(),
    };
  }

  if (pathname === '/api/reconcile/payees' && method === 'POST') {
    const inn = String(body.inn);
    const supplier = SUPPLIERS.find((x) => x.id === Number(body.supplier_id)) ?? IMMEDIATE.find((x) => x.id === Number(body.supplier_id));
    unknown = unknown.filter((u) => u.inn !== inn);
    return { message: `Запомнил: ИНН ${inn} — ${supplier?.name ?? 'поставщик'}.`, plan: plan() };
  }

  let match = /^\/api\/reconcile\/suppliers\/(\d+)$/.exec(pathname);
  if (match) return ledger(Number(match[1]), params);

  match = /^\/api\/reconcile\/suppliers\/(\d+)\/acts$/.exec(pathname);
  if (match && method === 'POST') {
    const supplierId = Number(match[1]);
    if (!SUPPLIERS.some((x) => x.id === supplierId)) {
      throw new ApiError(400, 'bad', 'Акт можно загрузить только поставщику с отсрочкой.');
    }
    const name = params.get('name') ?? 'акт.pdf';
    const id = nextAct++;
    const scan = /скан|scan/i.test(name);
    const act = simpleAct(id, supplierId, String(id), new Date().toISOString().slice(0, 10), '2026-07-01', '15400.00', '15400.00', [], scan ? 'partial' : 'full');
    act.file_name = name;
    acts.set(id, act);
    return { act: structuredClone(act) };
  }

  match = /^\/api\/reconcile\/acts\/(\d+)(?:\/(resolve|delete))?$/.exec(pathname);
  if (match) {
    const id = Number(match[1]);
    const act = acts.get(id);
    if (!act) throw new ApiError(404, 'not found', 'Акт не найден.');
    if (!match[2]) return structuredClone(act);
    if (match[2] === 'delete') {
      acts.delete(id);
      return { message: 'Акт удалён.', supplier_id: act.supplier_id };
    }
    const key = String(body.key);
    const reason = String(body.reason ?? '').trim();
    if (!reason) throw new ApiError(400, 'bad', 'Напишите причину: что выяснили.');
    const resolved = { reason, by: ME, at: new Date().toISOString() };
    for (const l of act.lines) if (l.issue?.key === key) l.issue.resolved = resolved;
    for (const e of act.extra) if (e.key === key) e.resolved = resolved;
    act.status = actStatus(act);
    if (act.status === 'ok') {
      act.totals = { ...act.totals, explained_fully: true };
    }
    return structuredClone(act);
  }

  return undefined;
}
